import { useState } from 'react';
import { type DirectedOutput, buildDirectedProject } from '../../director/director';
import { LLM_PROVIDERS, generateDirectedShots } from '../../director/llm-director';
import { parseScript } from '../../director/script-parser';
import { planScenes } from '../../director/shot-planner';
import { generateShotArtDataUrl } from '../../media/asset-generator';
import { buildDirectedShots } from '../../media/render-engine';
import type { StudioAsset, StudioClip, StudioTrack } from '../../media/types';
import { useLlmConfig } from '../../store/llm-config-store';
import { useStudio } from '../../store/studio-store';

const EXAMPLE_SCRIPT = `Título: A Última Fronteira

INT. NAVE - NOITE

A nave desliza em silêncio pelo vácuo escuro. Poeira estelar reflete nas janelas.

MARIA
Onde estamos?

O capitão olha para o horizonte de estrelas sem responder.

EXT. PLANETA - AMANHECER

Luz dourada banha um vale desconhecido. Maria caminha devagar, respirando o ar estranho.

MARIA
Este é o começo.

INT. CAVERNA - DIA

Sombras se movem no fundo da caverna. Algo acorda.

MARIA
Não estamos sozinhos.

FADE OUT`;

interface PipelineStep {
  label: string;
  ok: boolean;
  detail: string;
}

export function ScriptPanel() {
  const scriptText = useStudio((s) => s.scriptText);
  const setScriptText = useStudio((s) => s.setScriptText);
  const setProject = useStudio((s) => s.setProject);
  const setView = useStudio((s) => s.setView);
  const selectedFormat = useStudio((s) => s.selectedFormat);
  const setFormat = useStudio((s) => s.setFormat);
  const loadMediaSession = useStudio((s) => s.loadMediaSession);

  const llmConfig = useLlmConfig((s) => s);
  const [showKey, setShowKey] = useState(false);

  const [steps, setSteps] = useState<PipelineStep[]>([]);
  const [summary, setSummary] = useState<{ scenes: number; shots: number; seconds: number } | null>(
    null
  );
  const [generated, setGenerated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const llmEnabled = llmConfig.apiKey.trim().length > 0;

  async function handleGenerate() {
    setBusy(true);
    setError(null);
    setSummary(null);
    setGenerated(false);
    setSteps([]);

    const pipeline: PipelineStep[] = [];
    try {
      const scenes = parseScript(scriptText);
      if (scenes.length === 0) {
        throw new Error('Roteiro vazio: escreva pelo menos uma cena.');
      }
      pipeline.push({ label: 'Script → storyboard', ok: true, detail: `${scenes.length} cena(s)` });

      let shots: ReturnType<typeof planScenes>;
      if (llmEnabled) {
        try {
          shots = await generateDirectedShots(
            {
              provider: llmConfig.provider,
              apiKey: llmConfig.apiKey,
              model: llmConfig.model,
            },
            scenes
          );
          pipeline.push({
            label: 'AI Director (LLM) → shot plan',
            ok: true,
            detail: `${shots.length} plano(s) dirigidos`,
          });
        } catch (err) {
          const detail = err instanceof Error ? err.message : 'erro desconhecido';
          pipeline.push({ label: 'AI Director (LLM) → shot plan', ok: false, detail });
          shots = planScenes(scenes);
          pipeline.push({
            label: 'Fallback → direção heurística',
            ok: true,
            detail: `${shots.length} plano(s)`,
          });
        }
      } else {
        shots = planScenes(scenes);
        pipeline.push({
          label: 'AI Director (heurístico) → shot plan',
          ok: true,
          detail: `${shots.length} plano(s)`,
        });
      }

      const result = buildDirectedProject(scenes, shots, {
        title: extractFirstTitle(scenes),
        format: selectedFormat
          ? {
              preset: selectedFormat.id,
              width: selectedFormat.defaultResolution.width,
              height: selectedFormat.defaultResolution.height,
              fps: selectedFormat.fps,
            }
          : undefined,
      });

      pipeline.push({ label: 'Shot plan → Video DSL', ok: true, detail: 'projeto gerado' });
      pipeline.push({ label: 'Schema validation', ok: true, detail: 'sem erros' });
      pipeline.push({
        label: 'Compiler → Cinematic Engine',
        ok: true,
        detail: `${result.durationSeconds}s · ${result.project.format.width}×${result.project.format.height} @ ${result.project.format.fps}fps`,
      });

      const media = buildMediaFromShots(result, shots);
      loadMediaSession(media.assets, media.tracks);
      pipeline.push({
        label: 'Asset generation',
        ok: true,
        detail: `${media.assets.length} arte(s) gerada(s) + ${media.clips} clip(s)`,
      });

      setSteps(pipeline);
      setSummary({
        scenes: result.sceneCount,
        shots: result.shotCount,
        seconds: result.durationSeconds,
      });
      setProject(result.compiled);
      if (selectedFormat) setFormat(selectedFormat);
      setGenerated(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao dirigir o roteiro');
      setSteps(pipeline);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel">
      <div className="panel-header">
        <h2>Script → AI Director</h2>
        <p className="panel-subtitle">
          Cole um roteiro em linguagem natural; o AI Director gera storyboard, shot plan e Video
          DSL. Configure uma API key abaixo para usar um LLM real — sem chave, a direção heurística
          offline é usada.
        </p>
      </div>

      <section className="panel-section">
        <h3>AI Director</h3>
        <div className="llm-config">
          <label>
            <span>Provider</span>
            <select
              value={llmConfig.provider}
              onChange={(e) => llmConfig.setProvider(e.target.value as typeof llmConfig.provider)}
              disabled={busy}
            >
              {Object.entries(LLM_PROVIDERS).map(([id, info]) => (
                <option key={id} value={id}>
                  {info.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Modelo</span>
            <input
              type="text"
              value={llmConfig.model}
              onChange={(e) => llmConfig.setModel(e.target.value)}
              disabled={busy}
              placeholder={LLM_PROVIDERS[llmConfig.provider].defaultModel}
            />
          </label>
          <label>
            <span>API key</span>
            <div className="llm-key-row">
              <input
                type={showKey ? 'text' : 'password'}
                value={llmConfig.apiKey}
                onChange={(e) => llmConfig.setApiKey(e.target.value)}
                disabled={busy}
                placeholder={LLM_PROVIDERS[llmConfig.provider].apiKeyHint}
                autoComplete="off"
              />
              <button
                type="button"
                className="ghost-btn"
                onClick={() => setShowKey((v) => !v)}
                aria-label={showKey ? 'Ocultar chave' : 'Mostrar chave'}
              >
                {showKey ? '🙈' : '👁'}
              </button>
            </div>
          </label>
          <div className={`llm-status${llmEnabled ? ' enabled' : ''}`}>
            {llmEnabled ? '✓ LLM ativo' : 'Modo atual: direção heurística (offline)'} — a chave fica
            apenas no seu navegador.
          </div>
        </div>
      </section>

      <section className="panel-section">
        <div className="action-row script-actions">
          <button
            type="button"
            className="ghost-btn"
            onClick={() => setScriptText(EXAMPLE_SCRIPT)}
            disabled={busy}
          >
            Usar exemplo
          </button>
        </div>
        <textarea
          className="script-textarea"
          value={scriptText}
          onChange={(e) => setScriptText(e.target.value)}
          placeholder={'Escreva seu roteiro…\n\nINT. CENA - LOCAL\nAção.\n\nPERSONAGEM\nFala.'}
          spellCheck={false}
        />
        <div className="action-row">
          <button
            type="button"
            className="primary-btn"
            onClick={() => void handleGenerate()}
            disabled={busy || scriptText.trim().length === 0}
          >
            {busy ? 'Dirigindo…' : '🎬 Gerar Vídeo'}
          </button>
        </div>
      </section>

      {error && (
        <section className="panel-section">
          <h3>Erro</h3>
          <pre className="render-log script-error">{error}</pre>
        </section>
      )}

      {steps.length > 0 && (
        <section className="panel-section">
          <h3>Pipeline</h3>
          <ol className="pipeline-list">
            {steps.map((step, index) => (
              <li
                key={`${step.label}-${index}`}
                className={`pipeline-step${step.ok ? '' : ' failed'}`}
              >
                <span className="pipeline-status">{step.ok ? '✓' : '✗'}</span>
                <span className="pipeline-label">{step.label}</span>
                <span className="pipeline-detail">{step.detail}</span>
              </li>
            ))}
          </ol>
          {summary && (
            <div className="pipeline-summary">
              {summary.scenes} cenas · {summary.shots} shots · {summary.seconds}s — pronto para
              preview.
            </div>
          )}
          {generated && (
            <div className="action-row pipeline-open">
              <button type="button" className="primary-btn" onClick={() => setView('canvas')}>
                ▶ Abrir no Canvas
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function extractFirstTitle(scenes: { title: string }[]): string {
  const clean = scenes[0]?.title ?? '';
  return (
    clean
      .replace(/^(cena|scene)\s*\d*\s*[:.-]*\s*/i, '')
      .replace(/^(int\.|ext\.|int\.\/ext\.|int\/ext)[\s.:-]*/i, '')
      .trim() || 'Projeto Dirigido'
  );
}

function buildMediaFromShots(
  result: DirectedOutput,
  shots: Array<{ title: string; emotion: string }>
): { assets: StudioAsset[]; tracks: StudioTrack[]; clips: number } {
  const { width, height } = result.project.format;
  const directed = buildDirectedShots(result.compiled.scenes);

  const assets: StudioAsset[] = shots.map((shot, index) => ({
    id: `asset-shot-${index + 1}`,
    name: shot.title,
    kind: 'image',
    mimeType: 'image/svg+xml',
    size: 0,
    url: null,
    dataUrl: generateShotArtDataUrl({
      title: shot.title,
      emotion: shot.emotion,
      index,
      width,
      height,
    }),
    width,
    height,
    createdAt: Date.now(),
  }));

  const trackId = 'track-video-1';
  const clips: StudioClip[] = directed.map((directedShot, index) => ({
    id: `clip-shot-${index + 1}`,
    trackId,
    assetId: `asset-shot-${index + 1}`,
    start: directedShot.start,
    duration: Math.max(0.5, directedShot.duration),
    trimStart: 0,
    trimEnd: 0,
  }));

  return {
    assets,
    tracks: [{ id: trackId, kind: 'video', name: 'Video 1', clips }],
    clips: clips.length,
  };
}
